import React, { useState } from 'react';
import { Fab, Tooltip } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useUserStore } from 'store';
import ChatPanel from './ChatPanel';
import ChatLargeDialog from './ChatLargeDialog';
import useHelpChat from './useHelpChat';
import SupportRequestDialog from './SupportRequestDialog';
import HelpVideosDialog from './HelpVideosDialog';
import DismissConfirmDialog from './DismissConfirmDialog';

function HelpWidget() {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const location = useLocation();

  const [chatOpen, setChatOpen] = useState(false);
  // The same conversation shown large in a dialog, from the panel's expand button
  const [largeOpen, setLargeOpen] = useState(false);
  const chat = useHelpChat();
  // null = plain form; an object = prefilled from a chat message the assistant could not
  // answer, with the chat attached (see ChatPanel.escalationPrefill)
  const [supportPrefill, setSupportPrefill] = useState(null);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [videosDialogOpen, setVideosDialogOpen] = useState(false);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);

  // Don't render if user has dismissed the widget, nor on the support page, which embeds the
  // same chat (two avustajas on one screen would only confuse)
  if (!user || user.help_widget_dismissed || location.pathname === '/support') {
    return null;
  }

  const handleFabClick = () => {
    setChatOpen((prev) => !prev);
  };

  const openSupport = (prefill = null) => {
    setSupportPrefill(prefill);
    setSupportDialogOpen(true);
  };

  return (
    <>
      {chatOpen && !largeOpen && (
        <ChatPanel
          variant="floating"
          messages={chat.messages}
          loading={chat.loading}
          onSendMessage={chat.sendMessage}
          onSuggestionClick={chat.sendMessage}
          onNewChat={chat.newChat}
          onMinimize={() => setChatOpen(false)}
          onExpand={() => setLargeOpen(true)}
          onOpenSupport={openSupport}
          onOpenVideos={() => setVideosDialogOpen(true)}
          onOpenDismiss={() => setDismissDialogOpen(true)}
        />
      )}

      <ChatLargeDialog
        open={largeOpen}
        onClose={() => {
          setLargeOpen(false);
          setChatOpen(false);
        }}
        onShrink={() => {
          setLargeOpen(false);
          setChatOpen(true);
        }}
        chat={chat}
        onOpenSupport={openSupport}
        onOpenVideos={() => setVideosDialogOpen(true)}
      />

      <Tooltip title={chatOpen ? t('helpChatbot.minimize') : t('helpChatbot.title')}>
        <Fab
          color="primary"
          aria-label={chatOpen ? t('helpChatbot.minimize') : t('helpChatbot.title')}
          onClick={handleFabClick}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
          }}
        >
          {chatOpen ? <KeyboardArrowDownIcon /> : <ChatBubbleOutlineIcon />}
        </Fab>
      </Tooltip>

      <SupportRequestDialog
        open={supportDialogOpen}
        prefill={supportPrefill}
        onClose={() => setSupportDialogOpen(false)}
      />

      <HelpVideosDialog
        open={videosDialogOpen}
        onClose={() => setVideosDialogOpen(false)}
      />

      <DismissConfirmDialog
        open={dismissDialogOpen}
        onClose={() => setDismissDialogOpen(false)}
      />
    </>
  );
}

export default HelpWidget;
